// =============================================================================
// MeuDriver — TripLivePanel (lado aluno)
// -----------------------------------------------------------------------------
// "Onde está meu instrutor?" — mapa ao vivo + ETA, dentro do detalhe da aula.
//
// Regras de honestidade da UI, que valem mais que o visual aqui:
//   * Sem posição ao vivo, NÃO finge: mostra o ponto de encontro e diz que o
//     instrutor ainda não iniciou.
//   * ETA aproximado é rotulado como aproximado (o fallback de Haversine é bem
//     diferente de roteamento real).
//   * Sinal parado há mais de 1 min vira aviso explícito, em vez de deixar o
//     aluno olhando um carro congelado achando que é a posição atual.
// =============================================================================

import { MapPin, Navigation, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { TripLiveMap } from '@/components/shared/TripLiveMap'
import { useTripLive } from '@/hooks/useTripLive'
import { formatEta, formatDistance } from '@/lib/eta'

interface Props {
  bookingId: string
  pickupLat: number | null
  pickupLng: number | null
  pickupAddress?: string | null
  instructorName?: string | null
}

export function TripLivePanel({
  bookingId,
  pickupLat,
  pickupLng,
  pickupAddress,
  instructorName,
}: Props) {
  const hasPickup =
    typeof pickupLat === 'number' &&
    typeof pickupLng === 'number' &&
    Number.isFinite(pickupLat) &&
    Number.isFinite(pickupLng)

  const { status, position, eta, isLoading, isStale } = useTripLive(
    bookingId,
    hasPickup ? { lat: pickupLat!, lng: pickupLng! } : null,
  )

  // Sem coordenada do ponto de encontro não há o que mapear.
  if (!hasPickup) return null

  // Antes de sair e depois de embarcar, o painel não tem função.
  if (status === 'embarcado' || status === 'encerrado' || status === 'cancelado') return null

  const isEnRoute = status === 'a_caminho'
  const hasArrived = status === 'chegou'

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Navigation className="h-4 w-4 text-brand-600" />
            Acompanhe seu instrutor
          </CardTitle>
          {hasArrived && (
            <Badge className="bg-green-100 text-green-800 border-green-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Chegou
            </Badge>
          )}
          {isEnRoute && eta && (
            <Badge className="bg-brand-50 text-brand-700 border-brand-100">
              {eta.approximate ? '~' : ''}
              {formatEta(eta.durationSeconds)}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-[280px] w-full rounded-xl" />
        ) : (
          <TripLiveMap
            position={position}
            pickupLat={pickupLat!}
            pickupLng={pickupLng!}
            pickupAddress={pickupAddress}
            instructorName={instructorName}
            height={280}
          />
        )}

        {status === 'nao_iniciado' && (
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            O instrutor ainda não iniciou o deslocamento. Quando ele sair, o carro
            aparece aqui em tempo real.
          </p>
        )}

        {hasArrived && (
          <p className="text-sm text-green-700 font-medium">
            Seu instrutor já está no ponto de encontro.
          </p>
        )}

        {isEnRoute && eta && (
          <p className="text-sm text-muted-foreground">
            {eta.approximate ? 'Aproximadamente ' : 'Cerca de '}
            <strong>{formatEta(eta.durationSeconds)}</strong> · {formatDistance(eta.distanceMeters)}
            {eta.approximate && (
              <span className="block text-xs mt-0.5">
                Estimativa em linha reta — o tempo real pode variar com o trânsito.
              </span>
            )}
          </p>
        )}

        {isStale && isEnRoute && (
          <p className="text-sm text-amber-700 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            O sinal do instrutor não atualiza há mais de um minuto. A posição no mapa
            pode estar defasada.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export default TripLivePanel
